import { useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Boxes,
  Check,
  CheckCircle2,
  Clock,
  ExternalLink,
  Eye,
  FileText,
  Filter,
  Layers,
  LayoutDashboard,
  List,
  ListChecks,
  Package,
  RotateCcw,
  Sparkles,
  Timer,
  TrendingUp,
  X,
  XCircle,
} from "lucide-react";
import { Badge, Button, KpiCard, type KpiItem } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import type { SemanticTone } from "@/shared/ui/core/types";

/* =========================================================================
   NEW KPI CARD COMPONENT (Inspired by User's Reference Image)
   ========================================================================= */

export type NewKpiTone = SemanticTone;

export interface NewKpiCardProps {
  id: string;
  label: string;
  value: string | number;
  unit?: string;
  icon?: React.ReactNode;
  tone: NewKpiTone;
  selected?: boolean;
  onClick?: () => void;
  hint?: string;
  badge?: string;
  variant?: "flat" | "circle-icon" | "accent-bar";
}

const toneStyles: Record<
  SemanticTone,
  {
    title: string;
    value: string;
    icon: string;
    iconBg: string;
    border: string;
    borderActive: string;
    ringActive: string;
    accentBar: string;
  }
> = {
  neutral: {
    // Tổng: màu đen than / xám than thanh lịch chuẩn hệ thống, hoàn toàn không bị chói
    title: "text-text-secondary",
    value: "text-text",
    icon: "text-slate-500",
    iconBg: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    border: "border-border",
    borderActive: "border-slate-700 dark:border-slate-300",
    ringActive: "ring-2 ring-slate-500/20",
    accentBar: "bg-slate-600",
  },
  primary: {
    // Xanh lá đậm thương hiệu Galaxy One (Forest Green) đầm ấm, dịu mắt
    title: "text-primary",
    value: "text-primary",
    icon: "text-primary",
    iconBg: "bg-primary-soft text-primary",
    border: "border-border",
    borderActive: "border-primary",
    ringActive: "ring-2 ring-primary/25",
    accentBar: "bg-primary",
  },
  accent: {
    title: "text-accent-fg",
    value: "text-accent-fg",
    icon: "text-accent-fg",
    iconBg: "bg-accent-soft text-accent-fg",
    border: "border-border",
    borderActive: "border-accent",
    ringActive: "ring-2 ring-accent/25",
    accentBar: "bg-accent",
  },
  success: {
    // Hoàn thành: đồng bộ 100% với màu xanh lá Forest Green của thẻ Hoàn tất cũ
    title: "text-primary",
    value: "text-primary",
    icon: "text-primary",
    iconBg: "bg-primary-soft text-primary",
    border: "border-border",
    borderActive: "border-primary",
    ringActive: "ring-2 ring-primary/25",
    accentBar: "bg-primary",
  },
  warning: {
    // Đang mở / Chờ: màu cam đất / hổ phách trầm ấm
    title: "text-warning-fg",
    value: "text-warning-fg",
    icon: "text-warning-fg",
    iconBg: "bg-warning-soft text-warning-fg",
    border: "border-border",
    borderActive: "border-warning",
    ringActive: "ring-2 ring-warning/25",
    accentBar: "bg-warning",
  },
  danger: {
    // Từ chối / Quá hạn: màu đỏ thẫm trầm
    title: "text-danger-fg",
    value: "text-danger-fg",
    icon: "text-danger-fg",
    iconBg: "bg-danger-soft text-danger-fg",
    border: "border-border",
    borderActive: "border-danger",
    ringActive: "ring-2 ring-danger/25",
    accentBar: "bg-danger",
  },
  info: {
    // Đang xử lý / Đang sản xuất: màu navy trầm ấm của hệ thống
    title: "text-info-fg",
    value: "text-info-fg",
    icon: "text-info-fg",
    iconBg: "bg-info-soft text-info-fg",
    border: "border-border",
    borderActive: "border-info",
    ringActive: "ring-2 ring-info/25",
    accentBar: "bg-info",
  },
  hold: {
    title: "text-danger-fg",
    value: "text-danger-fg",
    icon: "text-danger-fg",
    iconBg: "bg-danger-soft text-danger-fg",
    border: "border-border",
    borderActive: "border-danger",
    ringActive: "ring-2 ring-danger/25",
    accentBar: "bg-danger",
  },
  draft: {
    title: "text-slate-600 dark:text-slate-400",
    value: "text-slate-800 dark:text-slate-200",
    icon: "text-slate-500",
    iconBg: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    border: "border-border",
    borderActive: "border-slate-500",
    ringActive: "ring-2 ring-slate-400/20",
    accentBar: "bg-slate-500",
  },
};

export function NewKpiCard({
  label,
  value,
  unit,
  icon,
  tone,
  selected = false,
  onClick,
  hint,
  badge,
  variant = "flat",
}: NewKpiCardProps) {
  const styles = toneStyles[tone] || toneStyles.neutral;
  const isInteractive = Boolean(onClick);

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isInteractive && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        "group relative flex flex-col justify-between overflow-hidden rounded-control bg-surface p-3.5 sm:p-4 text-left transition-all duration-150",
        "border shadow-2xs",
        selected
          ? cn("border-2 shadow-sm -translate-y-0.5", styles.borderActive, styles.ringActive)
          : cn("border-border hover:border-slate-300 hover:shadow-xs dark:hover:border-slate-700"),
        isInteractive && "cursor-pointer active:scale-[0.99]",
      )}
    >
      {/* Optional Top Accent Bar for Variant accent-bar */}
      {variant === "accent-bar" && (
        <span
          className={cn(
            "absolute inset-x-0 top-0 h-1 transition-opacity",
            styles.accentBar,
            selected ? "opacity-100" : "opacity-75 group-hover:opacity-100",
          )}
        />
      )}

      {/* Top Header Row: Title & Right Icon */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 truncate">
          {selected && (
            <span className={cn("shrink-0", styles.icon)}>
              <CheckCircle2 className="size-4" strokeWidth={2.5} />
            </span>
          )}
          <span
            className={cn(
              "m-0 truncate text-[12px] font-bold uppercase tracking-wider",
              styles.title,
            )}
          >
            {label}
          </span>
        </div>

        {icon ? (
          <div className="shrink-0">
            {variant === "circle-icon" ? (
              <span
                className={cn(
                  "flex size-7 items-center justify-center rounded-full transition-transform group-hover:scale-110",
                  styles.iconBg,
                )}
              >
                {icon}
              </span>
            ) : (
              <span className={cn("inline-flex transition-transform group-hover:scale-110", styles.icon)}>
                {icon}
              </span>
            )}
          </div>
        ) : null}
      </div>

      {/* Bottom Row: Number + Unit + Optional Hint */}
      <div className="mt-2.5 flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-1.5 overflow-hidden">
          <span
            className={cn(
              "font-mono text-2xl font-bold tracking-tight sm:text-3xl",
              styles.value,
            )}
          >
            {value}
          </span>
          {unit ? (
            <span className="truncate text-caption font-normal text-text-muted">
              {unit}
            </span>
          ) : null}
        </div>

        {badge ? (
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px] font-semibold",
              styles.iconBg,
            )}
          >
            {badge}
          </span>
        ) : hint ? (
          <span className="text-[11px] text-text-muted truncate">{hint}</span>
        ) : null}
      </div>
    </div>
  );
}

/* =========================================================================
   MAIN TESTUI TAB 10 SHOWCASE SECTION
   ========================================================================= */

type DatasetKey = "requests" | "sales" | "manufacturing";

export function TestUiKpiSection() {
  const [dataset, setDataset] = useState<DatasetKey>("requests");
  const [selectedId, setSelectedId] = useState<string>("total");
  const [newCardVariant, setNewCardVariant] = useState<"flat" | "circle-icon" | "accent-bar">("flat");

  // Dữ liệu mẫu 1: Theo đúng ảnh đính kèm (Tổng yêu cầu, Đang xử lý, Hoàn thành, Từ chối)
  const requestCards = [
    {
      id: "total",
      label: "TỔNG YÊU CẦU",
      value: "3878",
      unit: "yêu cầu",
      oldTone: "neutral" as SemanticTone,
      newTone: "neutral" as NewKpiTone,
      oldIcon: List,
      newIcon: <List className="size-5" strokeWidth={2} />,
      circleIcon: <List className="size-4" strokeWidth={2} />,
    },
    {
      id: "in_progress",
      label: "ĐANG XỬ LÝ",
      value: "0",
      unit: "yêu cầu",
      oldTone: "info" as SemanticTone,
      newTone: "info" as NewKpiTone,
      oldIcon: Clock,
      newIcon: <Clock className="size-5" strokeWidth={2} />,
      circleIcon: <Clock className="size-4" strokeWidth={2} />,
    },
    {
      id: "completed",
      label: "HOÀN THÀNH",
      value: "3865",
      unit: "yêu cầu",
      oldTone: "primary" as SemanticTone,
      newTone: "primary" as NewKpiTone,
      oldIcon: CheckCircle2,
      newIcon: <CheckCircle2 className="size-5" strokeWidth={2} />,
      circleIcon: <Check className="size-4" strokeWidth={2.5} />,
    },
    {
      id: "rejected",
      label: "TỪ CHỐI",
      value: "13",
      unit: "yêu cầu",
      oldTone: "danger" as SemanticTone,
      newTone: "danger" as NewKpiTone,
      oldIcon: XCircle,
      newIcon: <XCircle className="size-5" strokeWidth={2} />,
      circleIcon: <X className="size-4" strokeWidth={2.5} />,
    },
  ];

  // Dữ liệu mẫu 2: Đơn bán hàng (Sales Orders - Đang dùng thực tế tại Galaxy)
  const salesCards = [
    {
      id: "so_total",
      label: "TỔNG ĐƠN BÁN",
      value: "37",
      unit: "đơn",
      oldTone: "neutral" as SemanticTone,
      newTone: "neutral" as NewKpiTone,
      oldIcon: Layers,
      newIcon: <Layers className="size-5" strokeWidth={2} />,
      circleIcon: <Layers className="size-4" strokeWidth={2} />,
    },
    {
      id: "so_open",
      label: "ĐANG MỞ",
      value: "16",
      unit: "đơn",
      oldTone: "warning" as SemanticTone,
      newTone: "warning" as NewKpiTone,
      oldIcon: FileText,
      newIcon: <FileText className="size-5" strokeWidth={2} />,
      circleIcon: <FileText className="size-4" strokeWidth={2} />,
    },
    {
      id: "so_in_prod",
      label: "ĐANG SẢN XUẤT",
      value: "1",
      unit: "đơn",
      oldTone: "info" as SemanticTone,
      newTone: "info" as NewKpiTone,
      oldIcon: Boxes,
      newIcon: <Boxes className="size-5" strokeWidth={2} />,
      circleIcon: <Boxes className="size-4" strokeWidth={2} />,
    },
    {
      id: "so_ready",
      label: "HOÀN TẤT",
      value: "4",
      unit: "đơn",
      oldTone: "primary" as SemanticTone,
      newTone: "primary" as NewKpiTone,
      oldIcon: CheckCircle2,
      newIcon: <CheckCircle2 className="size-5" strokeWidth={2} />,
      circleIcon: <Check className="size-4" strokeWidth={2.5} />,
    },
    {
      id: "so_overdue",
      label: "QUÁ HẠN",
      value: "14",
      unit: "đơn",
      oldTone: "danger" as SemanticTone,
      newTone: "danger" as NewKpiTone,
      oldIcon: AlertTriangle,
      newIcon: <AlertTriangle className="size-5" strokeWidth={2} />,
      circleIcon: <AlertTriangle className="size-4" strokeWidth={2} />,
    },
  ];

  // Dữ liệu mẫu 3: Lệnh sản xuất (Planning MO)
  const moCards = [
    {
      id: "mo_total",
      label: "TỔNG LỆNH SX",
      value: "48",
      unit: "lệnh",
      oldTone: "neutral" as SemanticTone,
      newTone: "neutral" as NewKpiTone,
      oldIcon: Package,
      newIcon: <Package className="size-5" strokeWidth={2} />,
      circleIcon: <Package className="size-4" strokeWidth={2} />,
    },
    {
      id: "mo_planned",
      label: "CHỜ CHẠY",
      value: "12",
      unit: "lệnh",
      oldTone: "draft" as SemanticTone,
      newTone: "draft" as NewKpiTone,
      oldIcon: Clock,
      newIcon: <Clock className="size-5" strokeWidth={2} />,
      circleIcon: <Clock className="size-4" strokeWidth={2} />,
    },
    {
      id: "mo_running",
      label: "ĐANG CHẠY",
      value: "8",
      unit: "lệnh",
      oldTone: "info" as SemanticTone,
      newTone: "info" as NewKpiTone,
      oldIcon: Activity,
      newIcon: <Activity className="size-5" strokeWidth={2} />,
      circleIcon: <Activity className="size-4" strokeWidth={2} />,
    },
    {
      id: "mo_completed",
      label: "HOÀN THÀNH",
      value: "25",
      unit: "lệnh",
      oldTone: "primary" as SemanticTone,
      newTone: "primary" as NewKpiTone,
      oldIcon: CheckCircle2,
      newIcon: <CheckCircle2 className="size-5" strokeWidth={2} />,
      circleIcon: <Check className="size-4" strokeWidth={2.5} />,
    },
    {
      id: "mo_hold",
      label: "TẠM GIỮ / SỰ CỐ",
      value: "3",
      unit: "lệnh",
      oldTone: "danger" as SemanticTone,
      newTone: "danger" as NewKpiTone,
      oldIcon: AlertTriangle,
      newIcon: <AlertTriangle className="size-5" strokeWidth={2} />,
      circleIcon: <AlertTriangle className="size-4" strokeWidth={2} />,
    },
  ];

  const activeCards =
    dataset === "requests"
      ? requestCards
      : dataset === "sales"
        ? salesCards
        : moCards;

  return (
    <div className="space-y-8">
      {/* Top Banner & Controller */}
      <div className="rounded-control border border-border bg-surface p-5 sm:p-6 shadow-xs space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-border/70 pb-4">
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-lg bg-primary-soft text-primary shadow-xs">
              <LayoutDashboard className="size-5" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="m-0 text-h2 font-bold text-text">
                  10. So Sánh Thiết Kế CardKPI: UI Hiện Tại vs UI Mới
                </h2>
                <Badge tone="primary" label="New Concept" />
              </div>
              <p className="m-0 mt-0.5 text-caption text-text-secondary">
                So sánh trực quan giữa <strong>Dòng 1 (Khối màu đặc Solid hiện tại)</strong> và{" "}
                <strong>Dòng 2 (Nền sáng thanh lịch + Icon góc phải theo hình đính kèm)</strong>.
              </p>
            </div>
          </div>

          {/* Dataset Switcher */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-caption font-semibold text-text-muted">Bộ dữ liệu:</span>
            <div className="inline-flex rounded-control border border-border bg-bg p-0.5">
              <button
                type="button"
                className={cn(
                  "rounded px-2.5 py-1 text-caption font-semibold transition-colors",
                  dataset === "requests"
                    ? "bg-surface text-primary shadow-xs"
                    : "text-text-secondary hover:text-text",
                )}
                onClick={() => {
                  setDataset("requests");
                  setSelectedId("total");
                }}
              >
                1. Test
              </button>
              <button
                type="button"
                className={cn(
                  "rounded px-2.5 py-1 text-caption font-semibold transition-colors",
                  dataset === "sales"
                    ? "bg-surface text-primary shadow-xs"
                    : "text-text-secondary hover:text-text",
                )}
                onClick={() => {
                  setDataset("sales");
                  setSelectedId("so_total");
                }}
              >
                2. Đơn bán hàng (SO)
              </button>
              <button
                type="button"
                className={cn(
                  "rounded px-2.5 py-1 text-caption font-semibold transition-colors",
                  dataset === "manufacturing"
                    ? "bg-surface text-primary shadow-xs"
                    : "text-text-secondary hover:text-text",
                )}
                onClick={() => {
                  setDataset("manufacturing");
                  setSelectedId("mo_total");
                }}
              >
                3. Lệnh sản xuất (MO)
              </button>
            </div>
          </div>
        </div>

        {/* Sub-controls: Variant toggle & instruction */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-caption">
          <div className="flex items-center gap-2">
            <span className="text-text-muted">Kiểu icon ở UI Mới:</span>
            <div className="inline-flex gap-1.5">
              <button
                type="button"
                onClick={() => setNewCardVariant("flat")}
                className={cn(
                  "rounded border px-2 py-0.5 font-medium transition-colors",
                  newCardVariant === "flat"
                    ? "border-primary bg-primary-soft text-primary font-semibold"
                    : "border-border bg-surface text-text-secondary hover:bg-bg",
                )}
              >
                A. Icon trần (Giống ảnh mẫu 100%)
              </button>
              <button
                type="button"
                onClick={() => setNewCardVariant("circle-icon")}
                className={cn(
                  "rounded border px-2 py-0.5 font-medium transition-colors",
                  newCardVariant === "circle-icon"
                    ? "border-primary bg-primary-soft text-primary font-semibold"
                    : "border-border bg-surface text-text-secondary hover:bg-bg",
                )}
              >
                B. Icon trong vòng tròn (Circle Badge)
              </button>
              <button
                type="button"
                onClick={() => setNewCardVariant("accent-bar")}
                className={cn(
                  "rounded border px-2 py-0.5 font-medium transition-colors",
                  newCardVariant === "accent-bar"
                    ? "border-primary bg-primary-soft text-primary font-semibold"
                    : "border-border bg-surface text-text-secondary hover:bg-bg",
                )}
              >
                C. Thêm vạch màu đầu thẻ (Accent Bar)
              </button>
            </div>
          </div>

          <div className="text-text-muted flex items-center gap-2 font-mono">
            <span>Card đang chọn:</span>
            <Badge tone="primary" label={selectedId} />
            <button
              type="button"
              className="text-caption text-primary hover:underline ml-1"
              onClick={() => setSelectedId("")}
            >
              Bỏ chọn
            </button>
          </div>
        </div>
      </div>

      {/* ===================================================================
          ROW 1: CARDKPI UI MÀU HIỆN TẠI (Current Solid Style)
          =================================================================== */}
      <section className="space-y-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-full bg-slate-200 text-slate-800 text-[12px] font-bold">
              1
            </span>
            <h3 className="m-0 text-h3 font-bold text-text">
              DÒNG 1: CardKPI UI Màu Hiện Tại (Solid Fill Style)
            </h3>
            <span className="text-caption text-text-muted">
              — Khối màu nền đặc, tương phản đậm
            </span>
          </div>
          <span className="text-[12px] text-text-muted italic">
            * Bấm chuột vào thẻ để xem hiệu ứng Active / Selected
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
          {activeCards.map((card) => {
            const isSelected = selectedId === card.id;
            return (
              <div key={`old-${card.id}`} className="h-full">
                <KpiCard
                  variant="solid"
                  item={{
                    id: card.id,
                    label: card.label,
                    value: card.value,
                    hint: card.unit,
                    icon: card.oldIcon,
                    tone: card.oldTone,
                    selected: isSelected,
                    onClick: () => setSelectedId(card.id),
                  }}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* ===================================================================
          ROW 2: CARDKPI UI MỚI (New Light Style Inspired by Attached Image)
          =================================================================== */}
      <section className="space-y-3.5 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-full bg-primary text-white text-[12px] font-bold">
              2
            </span>
            <h3 className="m-0 text-h3 font-bold text-primary">
              DÒNG 2: CardKPI UI Mới
            </h3>
            <span className="text-caption text-text-muted">
              — Nền sáng thanh thoát, tiêu đề + số mang màu nhận diện, icon góc phải
            </span>
          </div>
          <Badge tone="success" label="Đề xuất mới" />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
          {activeCards.map((card) => {
            const isSelected = selectedId === card.id;
            return (
              <NewKpiCard
                key={`new-${card.id}`}
                id={card.id}
                label={card.label}
                value={card.value}
                unit={card.unit}
                icon={newCardVariant === "circle-icon" ? card.circleIcon : card.newIcon}
                tone={card.newTone}
                selected={isSelected}
                onClick={() => setSelectedId(card.id)}
                variant={newCardVariant}
              />
            );
          })}
        </div>
      </section>

      {/* ===================================================================
          ROW 3: SO SÁNH TRỰC DIỆN THEO CẶP (Direct Head-to-Head Comparison)
          =================================================================== */}
      <section className="rounded-control border border-border bg-surface p-5 sm:p-6 shadow-xs space-y-5">
        <div className="flex items-center justify-between border-b border-border/70 pb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            <h3 className="m-0 text-h3 font-bold text-text">
              So Sánh Chi Tiết Từng Cặp Chỉ Số
            </h3>
          </div>
          <span className="text-caption text-text-muted">
            Đặt cạnh nhau để đánh giá độ rõ ràng và tính khả dụng
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {activeCards.slice(0, 4).map((card) => {
            const isSelected = selectedId === card.id;
            return (
              <div
                key={`compare-${card.id}`}
                className="rounded-lg border border-border bg-bg/50 p-3 space-y-3"
              >
                {/* Header pair */}
                <div className="flex items-center justify-between border-b border-border/60 pb-2">
                  <span className="text-caption font-bold text-text">
                    {card.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedId(card.id)}
                    className="text-[11px] text-primary hover:underline font-semibold"
                  >
                    {isSelected ? "Đang chọn ✓" : "Chọn test"}
                  </button>
                </div>

                {/* Old style slot */}
                <div className="space-y-1">
                  <span className="text-[11px] font-semibold text-text-muted">
                    Style Hiện Tại (Solid):
                  </span>
                  <KpiCard
                    dense
                    variant="solid"
                    item={{
                      id: `cmp-old-${card.id}`,
                      label: card.label,
                      value: card.value,
                      hint: card.unit,
                      icon: card.oldIcon,
                      tone: card.oldTone,
                      selected: isSelected,
                    }}
                  />
                </div>

                {/* New style slot */}
                <div className="space-y-1">
                  <span className="text-[11px] font-semibold text-primary">
                    Style Mới (Light & Accent):
                  </span>
                  <NewKpiCard
                    id={`cmp-new-${card.id}`}
                    label={card.label}
                    value={card.value}
                    unit={card.unit}
                    icon={card.newIcon}
                    tone={card.newTone}
                    selected={isSelected}
                    variant={newCardVariant}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ===================================================================
          BẢNG ĐÁNH GIÁ ƯU / NHƯỢC ĐIỂM CỦA 2 PHONG CÁCH
          =================================================================== */}
      <section className="rounded-control border border-border bg-surface p-5 sm:p-6 shadow-xs space-y-4">
        <h3 className="m-0 text-h3 font-bold text-text">
          Bảng Đánh Giá & Phân Tích Kỹ Thuật UI/UX
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-body border-collapse">
            <thead>
              <tr className="border-b border-border bg-bg/70 text-caption font-bold text-text-secondary uppercase">
                <th className="py-2.5 px-3">Tiêu chí UI/UX</th>
                <th className="py-2.5 px-3">UI Màu Hiện Tại (Solid Fill)</th>
                <th className="py-2.5 px-3">UI Mới (Light Card)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60 text-caption">
              <tr>
                <td className="py-3 px-3 font-semibold text-text">Độ thoáng giao diện (Visual Weight)</td>
                <td className="py-3 px-3 text-text-secondary">
                  Nặng nề khi hiển thị 5 - 8 thẻ trên màn hình vì toàn bộ nền là màu đậm (xanh lá, đỏ, cam, xanh dương).
                </td>
                <td className="py-3 px-3 text-success-fg font-medium">
                  Rất thoáng đãng, nền trắng/surface nhẹ nhàng, dễ chịu cho mắt khi làm việc thời gian dài.
                </td>
              </tr>
              <tr>
                <td className="py-3 px-3 font-semibold text-text">Độ dễ đọc của số liệu (Readability)</td>
                <td className="py-3 px-3 text-text-secondary">
                  Chữ trắng trên nền đậm có độ tương phản cao nhưng chữ số dễ bị chìm vào mảng màu nền.
                </td>
                <td className="py-3 px-3 text-success-fg font-medium">
                  Chữ số to, đậm mang màu sắc nhận diện nổi bật trên nền trắng, dễ quét mắt tìm số liệu nhanh.
                </td>
              </tr>
              <tr>
                <td className="py-3 px-3 font-semibold text-text">Bố cục tiêu đề & Icon</td>
                <td className="py-3 px-3 text-text-secondary">
                  Icon nhỏ nằm cạnh chữ hoặc dưới góc, không tạo điểm nhấn rõ nét theo chiều ngang.
                </td>
                <td className="py-3 px-3 text-success-fg font-medium">
                  Bố cục cân xứng: Tiêu đề in hoa bên trái + Icon chuyên biệt góc phải, phân bổ thị giác cân bằng.
                </td>
              </tr>
              <tr>
                <td className="py-3 px-3 font-semibold text-text">Hiển thị đơn vị đo (Unit)</td>
                <td className="py-3 px-3 text-text-secondary">
                  Thường chỉ hiển thị số trần hoặc nằm rời rạc trong phần hint nhỏ.
                </td>
                <td className="py-3 px-3 text-success-fg font-medium">
                  Đơn vị đặt ngay sau số liệu (ví dụ: <code>3878 yêu cầu</code>, <code>37 đơn</code>) rất tự nhiên và rõ nghĩa.
                </td>
              </tr>
              <tr>
                <td className="py-3 px-3 font-semibold text-text">Trạng thái Active / Filter</td>
                <td className="py-3 px-3 text-text-secondary">
                  Nâng cao thẻ (-translate-y-1) và đổ bóng đậm.
                </td>
                <td className="py-3 px-3 text-success-fg font-medium">
                  Viền đậm theo màu tone + viền ring phát sáng nhẹ + nền pastel tinh tế, nhận diện rõ ràng thẻ đang lọc.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
